package dev.danko.roblox_craft_ai.uikit.components

import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.styleModifier
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.ms
import org.jetbrains.compose.web.css.px

data class TabItem<T>(
    val key: T,
    val label: String,
    val count: Int? = null,
    val icon: (@Composable () -> Unit)? = null
)

@Composable
fun <T> AppTabs(
    tabs: List<TabItem<T>>,
    activeTab: T,
    onTabSelected: (T) -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalAppTheme.current
    val palette = theme.colors

    Row(
        modifier = Modifier
            .flexWrap(org.jetbrains.compose.web.css.FlexWrap.Wrap)
            .gap(2.px)
            .attrsModifier { attr("role", "tablist") }
            .border(width = 1.px, style = LineStyle.Solid, color = palette.border)
            .borderRadius(theme.radii.md)
            .backgroundColor(palette.surfaceElevated)
            .padding(4.px)
            .then(modifier),
        verticalAlignment = Alignment.CenterVertically
    ) {
        tabs.forEach { tab ->
            val isActive = tab.key == activeTab
            var isHovered by remember { mutableStateOf(false) }

            // A real <button> so tabs work with the keyboard and screen readers.
            org.jetbrains.compose.web.dom.Button(
                Modifier
                    .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                    .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
                    .minHeight(36.px)
                    .border(0.px, LineStyle.None, Color.rgba(0, 0, 0, 0f))
                    .styleModifier { property("font-family", "inherit") }
                    .padding(leftRight = 0.85.cssRem, topBottom = 0.4.cssRem)
                    .borderRadius(theme.radii.sm)
                    .backgroundColor(
                        when {
                            isActive -> palette.accent
                            isHovered -> palette.surface
                            else -> Color.rgba(0, 0, 0, 0f)
                        }
                    )
                    .cursor(Cursor.Pointer)
                    .onClick { onTabSelected(tab.key) }
                    .onMouseEnter { isHovered = true }
                    .onMouseLeave { isHovered = false }
                    .transition(
                        Transition.of("background-color", 150.ms),
                        Transition.of("color", 150.ms)
                    )
                    .toAttrs {
                        attr("type", "button")
                        attr("role", "tab")
                        attr("aria-selected", isActive.toString())
                    }
            ) {
                if (tab.icon != null) {
                    Box(modifier = Modifier.margin(right = 0.4.cssRem)) {
                        tab.icon.invoke()
                    }
                }

                SpanText(
                    text = tab.label,
                    modifier = Modifier
                        .fontSize(0.85.cssRem)
                        .fontWeight(if (isActive) FontWeight.Bold else FontWeight.Medium)
                        .color(
                            when {
                                isActive -> Color.rgb(0xFFFFFF)
                                isHovered -> palette.textPrimary
                                else -> palette.textSecondary
                            }
                        )
                )

                if (tab.count != null) {
                    SpanText(
                        text = "${tab.count}",
                        modifier = Modifier
                            .margin(left = 0.45.cssRem)
                            .padding(leftRight = 0.4.cssRem, topBottom = 0.1.cssRem)
                            .borderRadius(theme.radii.full)
                            .backgroundColor(
                                if (isActive) Color.rgba(255, 255, 255, 0.25f)
                                else palette.surface
                            )
                            .fontSize(0.7.cssRem)
                            .fontWeight(FontWeight.Bold)
                            .color(if (isActive) Color.rgb(0xFFFFFF) else palette.textSecondary)
                    )
                }
            }
        }
    }
}
